import React from 'react';
import cn from 'classnames';
import DatePicker, { registerLocale } from 'react-datepicker';
import { enUS, fr } from 'date-fns/locale';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import styles from './FormFields.module.scss';
import dayjs from 'dayjs';

// Register locales for react-datepicker
registerLocale('en', enUS);
registerLocale('fr', fr);

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DD h:mm a';

interface CustomDatePickerInputProps {
  onClick?: () => void;
  value?: string;
  [key: string]: any;
}

const CustomDatePickerInput = React.forwardRef<HTMLInputElement, CustomDatePickerInputProps>(
  ({ onClick, value, ...props }, ref) => (
    <input
      type="text"
      onClick={onClick}
      value={value}
      ref={ref}
      {...props}
      data-cy="datepicker"
    />
  )
);

interface DayPickerFieldProps {
  name: string;
  disabledBefore?: Date;
  disabledAfter?: Date;
  className?: string;
  placeholderText?: string;
  onChange?: () => void;
  disabled?: boolean;
}

const DayPickerField: React.FC<DayPickerFieldProps> = ({
  name,
  disabledBefore,
  disabledAfter,
  className,
  placeholderText,
  onChange = () => {},
  ...props
}) => {
  const { i18n } = useTranslation();
  const { setFieldValue, values, validate } = useFormikContext();
  const currentValue = values[name];
  const currentTimeValue = values[`${name}Time`];
  const selectedDay = currentValue ? new Date(currentValue) : undefined;
  
  // Get current locale for react-datepicker
  const currentLocale = i18n.language === 'fr' ? 'fr' : 'en';

  const handleChange = (value: Date | null) => {
    if (!value) {
      setFieldValue(name, null);
    }

    const day = dayjs(value).format(DATE_FORMAT);
    const format = currentTimeValue ? DATE_TIME_FORMAT : DATE_FORMAT;
    const combined = dayjs(`${day} ${currentTimeValue || ''}`, format);

    if (combined.isValid()) {
      setFieldValue(name, combined.toDate());

      if (onChange) {
        onChange();
      }
    }
  };

  return (
    <Field name={name} validate={validate}>
      {({ meta }) => (
        <>
          <DatePicker
            className={cn(className || styles.DayPickerField, {
              [styles.datepickerError]: meta.error && meta.touched
            })}
            customInput={
              <CustomDatePickerInput
                className={styles.datePickerInput}
                data-cy="datepicker"
              />
            }
            selected={selectedDay}
            onChange={handleChange}
            minDate={disabledBefore}
            maxDate={disabledAfter}
            dateFormat="yyyy-MM-dd"
            placeholderText={placeholderText}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            disabled={props.disabled}
            locale={currentLocale}
            {...props}
          />
          {/* {meta.error && meta.touched && ( */}
          {/*  <div className={styles.datepickerErrorMessage}>{meta.error}</div> */}
          {/* )} */}
        </>
      )}
    </Field>
  );
};



export default DayPickerField;
